// ──────────────────────────────────────────────────────────────────────────
// Central, CMS-ready data source for Amden Insights.
// ONE source of truth for every article — the Insights hub, the homepage
// "Latest insights" section, the article template, sitemap/RSS and the
// future website administration area all read from here.
//
// DEVELOPER NOTES
// - authorId is null on existing articles: the genuine author has not been
//   confirmed, so the byline honestly falls back to "Amden Group". Do NOT
//   assign an author without client confirmation.
// - body / toc / faq stay null/empty until genuine article content is
//   migrated. The article template renders them when present and stays
//   clean when they are not. Do not invent filler content.
// - readingTimeMinutes is derived from body length when a body exists;
//   it is hidden (not fabricated) when there is no body yet.
// - updatedAt stays null unless the content genuinely changed materially.
// - Never fabricate dates, metrics, authors, credentials or view counts.
// ──────────────────────────────────────────────────────────────────────────
import { getLive } from '@/lib/liveContent';

export const INSIGHTS_SEO = {
  title: 'Business Insights, Advice & Practical Guides | Amden Group',
  description:
    'Explore practical business insights from Amden Group across operations, people, systems, marketing, growth and starting a business.',
  path: '/insights',
};

export const ORGANISATION_BYLINE = 'Amden Group';

// Genuine, verified author profiles (from the About page). Articles are
// only linked to a person once the client confirms authorship.
export const AUTHORS = [
  {
    id: 'daniel-mcgivern',
    name: 'Daniel McGivern',
    role: 'Co-Founder & Director',
    bio: 'Daniel leads the operational and commercial direction of Amden Group, with over 15 years of experience supporting businesses through growth, operational change and strategic development.',
    expertise: ['Operations', 'Business strategy', 'Recruitment strategy', 'Systems implementation'],
    photo: {
      src: '/assets/founder-daniel.jpg',
      alt: 'Portrait of Daniel McGivern, Co-Founder & Director of Amden Group',
    },
    profileUrl: '/about',
    linkedIn: null,
  },
  {
    id: 'claire-mcgivern',
    name: 'Claire McGivern',
    role: 'Co-Founder & Director',
    bio: 'Claire specialises in HR, people development, training, coaching and organisational support, with over 20 years of experience across corporate and SME environments.',
    expertise: ['HR', 'People development', 'Training & coaching', 'Organisational support'],
    photo: {
      src: '/assets/founder-claire.jpg',
      alt: 'Portrait of Claire McGivern, Co-Founder & Director of Amden Group',
    },
    profileUrl: '/about',
    linkedIn: null,
  },
];

// Topic navigation. Service topics carry their division accent colour;
// cross-business topics use restrained navy.
export const INSIGHT_TOPICS = [
  { key: 'operations', label: 'Operations', color: '#FFB800', soft: '#FFF3CC', dark: '#9C7400', serviceHref: '/services/operations' },
  { key: 'people', label: 'People', color: '#D6318B', soft: '#FBDCEE', dark: '#A01E66', serviceHref: '/services/people' },
  { key: 'growth', label: 'Growth', color: '#029E88', soft: '#CCF2EC', dark: '#01755F', serviceHref: '/services/growth' },
  { key: 'launch', label: 'Launch', color: '#4225C4', soft: '#E4E0FA', dark: '#331BA0', serviceHref: '/services/launch' },
  { key: 'systems-technology', label: 'Systems & Technology', color: '#0B1729', soft: '#E2E8F2', dark: '#0B1729', serviceHref: null },
  { key: 'business-strategy', label: 'Business Strategy', color: '#0B1729', soft: '#E2E8F2', dark: '#0B1729', serviceHref: null },
  { key: 'leadership-management', label: 'Leadership & Management', color: '#0B1729', soft: '#E2E8F2', dark: '#0B1729', serviceHref: null },
];

const DEFAULTS = {
  authorId: null,
  secondaryTopics: [],
  tags: [],
  keywords: [],
  body: null,
  toc: [],
  faq: [],
  updatedAt: null,
  featured: false,
  recommended: false,
  status: 'published',
  relatedArticles: [],
  relatedServices: [],
  seo: null,
  og: null,
  redirectFrom: [],
  structuredDataType: 'BlogPosting',
};

const article = (fields) => ({ ...DEFAULTS, ...fields });

export const ARTICLES = [
  article({
    id: 'ins-hiring-before-job-ad',
    slug: 'hiring-problems-start-before-the-job-ad',
    title: 'Hiring Problems Usually Start Before the Job Ad',
    excerpt:
      'Most hiring problems don’t begin at interview stage — they begin much earlier, in how the role is defined, scoped and positioned.',
    primaryTopic: 'people',
    secondaryTopics: ['leadership-management'],
    tags: ['recruitment', 'hiring', 'hr'],
    keywords: ['recruitment problems', 'hiring process', 'job advertisement', 'role definition', 'sme recruitment'],
    image: {
      src: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1080&q=80',
      alt: 'Two colleagues reviewing hiring plans across a meeting table',
      width: 1080,
      height: 675,
    },
    publishedAt: '2026-06-18',
    relatedArticles: ['growing-business-systems-grow-with-it', 'before-you-spend-more-on-marketing'],
    relatedServices: ['people'],
  }),
  article({
    id: 'ins-systems-grow-with-business',
    slug: 'growing-business-systems-grow-with-it',
    title: 'If Your Business Is Growing, Your Systems Need To Grow With It',
    excerpt:
      'Growth exposes every weak process in a business. Here’s how to put structure in place before the cracks start to show.',
    primaryTopic: 'operations',
    secondaryTopics: ['systems-technology'],
    tags: ['systems', 'processes', 'scaling'],
    keywords: ['business systems', 'growing business', 'process improvement', 'operational structure', 'scaling operations'],
    image: {
      src: 'https://images.unsplash.com/photo-1620325867502-221cfb5faa5f?auto=format&fit=crop&w=1080&q=80',
      alt: 'Business operations framework mapped out on a whiteboard',
      width: 1080,
      height: 675,
    },
    publishedAt: '2026-05-27',
    relatedArticles: ['hiring-problems-start-before-the-job-ad', 'before-you-spend-more-on-marketing'],
    relatedServices: ['operations'],
  }),
  article({
    id: 'ins-marketing-four-things',
    slug: 'before-you-spend-more-on-marketing',
    title: 'Before You Spend More On Marketing, Fix These Four Things First',
    excerpt:
      'More budget won’t fix a weak foundation. Four commercial fundamentals to get right before increasing your marketing spend.',
    primaryTopic: 'growth',
    secondaryTopics: ['business-strategy'],
    tags: ['marketing', 'growth', 'strategy'],
    keywords: ['marketing spend', 'marketing budget', 'growth strategy', 'commercial fundamentals', 'marketing performance'],
    image: {
      src: 'https://images.unsplash.com/photo-1758518730083-4c12527b6742?auto=format&fit=crop&w=1080&q=80',
      alt: 'Consultant discussing growth strategy with a business team',
      width: 1080,
      height: 675,
    },
    publishedAt: '2026-05-06',
    relatedArticles: ['growing-business-systems-grow-with-it', 'hiring-problems-start-before-the-job-ad'],
    relatedServices: ['growth'],
  }),
];

// ── Centrally configurable editorial selections ──────────────────────────
// Pin a featured article by ID; when null, the hub temporarily uses the most
// recently published substantive article (never a short announcement).
export const FEATURED_ARTICLE_ID = null;

// Manually selected "Recommended reading" (max 4 shown). Only switch to
// view-based "Most Read" when genuine analytics data exists.
export const RECOMMENDED_ARTICLE_SLUGS = [
  'hiring-problems-start-before-the-job-ad',
  'growing-business-systems-grow-with-it',
  'before-you-spend-more-on-marketing',
];

// ── Browse by business challenge ──────────────────────────────────────────
// Each challenge deep-links to a filtered collection on the Insights hub.
// Future SEO topic landing pages (/insights/operations etc.) require unique
// introductory copy and real article depth before they go live.
export const BUSINESS_CHALLENGES = [
  { label: 'Improve operational efficiency', topic: 'operations' },
  { label: 'Introduce better systems', topic: 'systems-technology' },
  { label: 'Strengthen accountability', topic: 'operations' },
  { label: 'Recruit and retain people', topic: 'people' },
  { label: 'Develop managers and teams', topic: 'leadership-management' },
  { label: 'Improve marketing performance', topic: 'growth' },
  { label: 'Build a stronger website', topic: 'growth' },
  { label: 'Create a growth strategy', topic: 'business-strategy' },
  { label: 'Start or launch a business', topic: 'launch' },
];

// ── Guides & practical resources ──────────────────────────────────────────
// CRM/Stripe-ready resource model. NOTHING is invented here: with no
// published resources the live section stays hidden. Gated resources will
// collect name, business name, email, resource requested, marketing consent
// and source/campaign details through the secure enquiry service.
export const RESOURCE_TYPES = {
  checklist: 'Checklist',
  template: 'Template',
  planning_guide: 'Planning Guide',
  hr_guide: 'HR Guide',
  operational_review: 'Operational Review Tool',
  marketing_planner: 'Marketing Planner',
  startup_resource: 'Start-up Resource',
  pdf: 'PDF Download',
  assessment: 'Assessment',
};

export const INSIGHT_RESOURCES = [];

// ── Helpers ───────────────────────────────────────────────────────────────
// Live CMS data (published only) is the source of truth; bundled content is
// the logged offline fallback during migration.

// Brand placeholder keeps article cards intact when a published article has
// no featured image yet — the public site must never break over a missing
// optional CMS field.
const PLACEHOLDER_IMAGE = { src: '/assets/amden-logo-dark.png', width: 1080, height: 675 };

// Normalise every article once at the data layer: image, arrays and dates are
// guaranteed present so hub cards, homepage rails and article pages can render
// without defensive checks scattered across components.
const normalizeArticle = (a) => ({
  ...a,
  image: a.image?.src
    ? { width: 1080, height: 675, ...a.image, alt: a.image.alt || a.title || 'Amden Group insight' }
    : { ...PLACEHOLDER_IMAGE, alt: a.title || 'Amden Group insight' },
  excerpt: typeof a.excerpt === 'string' ? a.excerpt.trim() : '',
  secondaryTopics: Array.isArray(a.secondaryTopics) ? a.secondaryTopics : [],
  relatedArticles: Array.isArray(a.relatedArticles) ? a.relatedArticles : [],
  relatedServices: Array.isArray(a.relatedServices) ? a.relatedServices : [],
  toc: Array.isArray(a.toc) ? a.toc : [],
  faq: Array.isArray(a.faq) ? a.faq : [],
  body: Array.isArray(a.body) ? a.body : [],
  publishedAt: a.publishedAt || String(a.createdAt || '').slice(0, 10) || null,
});

const liveArticles = () => (getLive('articles') || ARTICLES).map(normalizeArticle);
const liveAuthors = () => getLive('bootstrap')?.authors || AUTHORS;
const liveInsightsConfig = () => getLive('bootstrap')?.settings?.insights || {};

export const topicFor = (key) => INSIGHT_TOPICS.find((t) => t.key === key) || null;

export const authorFor = (authorId) => liveAuthors().find((a) => (a.authorId || a.id) === authorId) || null;

export const bylineFor = (art) => authorFor(art.authorId)?.name || ORGANISATION_BYLINE;

export const getPublishedArticles = () =>
  liveArticles()
    .filter((a) => a.status === 'published')
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

export const getArticleBySlug = (slug) =>
  liveArticles().find((a) => a.slug === slug && a.status === 'published') || null;

export const getLatestArticles = (n = 3) => getPublishedArticles().slice(0, n);

export const getFeaturedArticle = () => {
  const published = getPublishedArticles();
  const pinnedId = liveInsightsConfig().featuredArticleId ?? FEATURED_ARTICLE_ID;
  const pinned = pinnedId ? published.find((a) => a.id === pinnedId) : null;
  const flagged = published.find((a) => a.featured);
  // Fallback: most recent substantive article (has a genuine excerpt).
  return pinned || flagged || published.find((a) => a.excerpt && a.excerpt.length > 60) || null;
};

export const getRecommendedArticles = (max = 4) => {
  const published = getPublishedArticles();
  const configured = liveInsightsConfig().recommendedArticleSlugs?.length
    ? liveInsightsConfig().recommendedArticleSlugs
    : RECOMMENDED_ARTICLE_SLUGS;
  const flagged = published.filter((a) => a.recommended);
  const selected = configured.map((slug) => published.find((a) => a.slug === slug)).filter(Boolean);
  return (selected.length ? selected : flagged).slice(0, max);
};

export const getRelatedArticles = (art, max = 2) => {
  const published = getPublishedArticles().filter((a) => a.id !== art.id);
  const explicit = art.relatedArticles
    .map((slug) => published.find((a) => a.slug === slug))
    .filter(Boolean);
  const rest = published.filter((a) => !explicit.includes(a));
  return [...explicit, ...rest].slice(0, max);
};

export const articleTopics = (art) =>
  [art.primaryTopic, ...art.secondaryTopics].map(topicFor).filter(Boolean);

export const formatInsightDate = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length > 10 ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Estimated reading time from genuine body content only. Returns null when
// no body exists yet — the UI hides the reading time rather than inventing it.
export const readingTimeFor = (art) => {
  if (!Array.isArray(art.body) || art.body.length === 0) return null;
  const words = art.body
    .map((b) => (b.type === 'ul' ? (b.items || []).join(' ') : b.text || ''))
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export const getPublishedResources = () =>
  INSIGHT_RESOURCES.filter((r) => r.status === 'published' && r.fileUrl);
